import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Allotment } from 'allotment';
import 'allotment/dist/style.css';
import { apiService } from '../services/api';
import { RequestJob, PriceBase, Subscription } from '../types';
import toast from 'react-hot-toast';
import LeftPanel from './LeftPanelNew';
import TableView from './TableView';
import Onboarding from './Onboarding';

/** ingest_warnings приходит JSON-строкой; кривой JSON не должен ронять экран. */
const parseWarnings = (raw?: string | null): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const Dashboard: React.FC = () => {
  const [requests, setRequests] = useState<RequestJob[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<RequestJob | null>(null);
  const [currentStagePath, setCurrentStagePath] = useState<string | null>(null); // preprocessed/analysis/merged path
  const [isLoading, setIsLoading] = useState(false);
  const [priceBase, setPriceBase] = useState<PriceBase | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!apiService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadRequests();
    loadPriceBase();
    loadBillingMeta();
  }, [navigate]);

  const loadBillingMeta = async () => {
    try {
      const [sub, me] = await Promise.all([
        apiService.getSubscription().catch(() => null),
        apiService.getMe().catch(() => null),
      ]);
      setSubscription(sub);
      setIsAdmin(!!me?.is_admin);
    } catch {
      // не критично
    }
  };

  const loadPriceBase = async () => {
    try {
      const pb = await apiService.getPriceBase();
      setPriceBase(pb);
    } catch {
      // No price base yet — that's ok
      setPriceBase(null);
    }
  };

  const loadRequests = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.listRequests();
      setRequests(data);
    } catch (error: any) {
      toast.error('Ошибка загрузки заявок');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestSelect = (req: RequestJob) => {
    setSelectedRequest(req);
    // Заявка на проверке — сразу открываем распознанное, это следующее действие
    setCurrentStagePath(req.status === 'awaiting_preview' ? (req.preview_path ?? null) : null);
    if (req.status === 'pending' || req.status === 'running') {
      pollRequest(req.id);
    }
  };
  

  const handleRequestUpload = async (files: File[]) => {
    if (!files.length) return;
    try {
      setIsLoading(true);
      const created = await apiService.uploadRequest(files);
      toast.success(files.length > 1
        ? `Загружено файлов: ${files.length}. Разбираем...`
        : 'Заявка загружена! Разбираем файл...');
      await loadRequests();
      setSelectedRequest(created);
      setCurrentStagePath(null);
      pollRequest(created.id);
    } catch (error: any) {
      if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || 'Для загрузки заявки нужна активная подписка');
        navigate('/billing');
      } else {
        toast.error(error.response?.data?.detail || 'Ошибка загрузки заявки');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const checkTaskStatus = (taskId: string, sessionId: string) => {
    const intervalId = setInterval(async () => {
      try {
        const status = await apiService.getTaskStatus(taskId);
        
        const isFinished = status.status === 'SUCCESS' || status.status === 'FAILURE';
        const isStageComplete = status.status === 'PENDING_CONFIRMATION' || status.status === 'AWAITING_BINDING_TABLE_CONFIRMATION';

        if (isFinished || isStageComplete) {
          if (status.status === 'SUCCESS') {
            toast.success('Обработка полностью завершена!');
          } else if (status.status === 'PENDING_CONFIRMATION') {
            toast.success('Первичная обработка завершена. Ожидается проверка типов.');
          } else if (status.status === 'AWAITING_BINDING_TABLE_CONFIRMATION') {
             toast.success('Таблица привязки создана. Ожидается проверка.');
          } else if (status.status === 'FAILURE') {
            toast.error(`Ошибка обработки: ${status.error || 'Неизвестная ошибка'}`);
          }
          
          clearInterval(intervalId);
          await loadRequests();

        }
      } catch (error) {
        toast.error('Ошибка проверки статуса задачи.');
        clearInterval(intervalId);
      }
    }, 5000);
  };

  const handleConfirmSelections = async (rows: any[]) => {
    if (!selectedRequest) return;
    try {
      setIsLoading(true);
      const { task_id } = await apiService.overwriteAnalysis(selectedRequest.id, rows);
      toast.success('Таблица сохранена, выполняется объединение...');
      const poll = async () => {
        const updated = await apiService.getRequest(selectedRequest.id);
        setSelectedRequest(updated);
        await loadRequests();
        if (!updated.bill_path) {
          setTimeout(poll, 2000);
        } else {
          setCurrentStagePath(updated.bill_path);
        }
      };
      poll();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setIsLoading(false);
    }
  };

  // Обновление заявки: бэкенд — источник правды, но не даём статусу
  // «откатиться» назад из-за гонки двух запросов подряд.
  const applyUpdate = (updated: RequestJob) => {
    const merge = (prev: RequestJob): RequestJob => {
      const keepReady = prev.status === 'ready' && updated.status !== 'ready' && updated.status !== 'failed';
      return { ...prev, ...updated, status: keepReady ? prev.status : updated.status };
    };
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? merge(r) : r)));
    setSelectedRequest((prev) => (prev && prev.id === updated.id ? merge(prev) : prev));
  };

  // Опрос до момента, когда от пользователя снова что-то требуется:
  // распознали и ждём проверки, получили результат анализа или упали.
  const pollRequest = (id: number) => {
    const tick = async () => {
      let updated: RequestJob;
      try {
        updated = await apiService.getRequest(id);
      } catch {
        setTimeout(tick, 5000);
        return;
      }
      applyUpdate(updated);

      if (updated.status === 'failed') {
        toast.error(updated.error || 'Не удалось обработать заявку');
        return;
      }
      if (updated.status === 'awaiting_preview' && updated.preview_path) {
        setCurrentStagePath((current) => current ?? updated.preview_path!);
        return;
      }
      if (updated.analysis_path) {
        return;
      }
      setTimeout(tick, 2000);
    };
    setTimeout(tick, 2000);
  };

  const handleConfirmPreview = async (rows: any[]) => {
    if (!selectedRequest) return;
    try {
      setIsConfirming(true);
      await apiService.confirmPreview(selectedRequest.id, rows);
      toast.success('Ищем позиции в вашем прайсе...');
      setCurrentStagePath(null);
      const updated = await apiService.getRequest(selectedRequest.id);
      applyUpdate(updated);
      pollRequest(selectedRequest.id);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Не удалось запустить поиск по прайсу');
    } finally {
      setIsConfirming(false);
    }
  };

  // legacy handlers removed

  const handleDeleteRequest = async (reqId: number) => {
    try {
      await apiService.deleteRequest(reqId);
      toast.success('Заявка удалена');
      if (selectedRequest?.id === reqId) {
        setSelectedRequest(null);
        setCurrentStagePath(null);
      }
      await loadRequests();
    } catch {
      toast.error('Ошибка удаления заявки');
    }
  };

  const handleDownloadFile = async (filePath: string, filename?: string) => {
    try {
      const blob = await apiService.downloadFile(filePath);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || filePath.split(/[/\\]/).pop() || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Ошибка скачивания файла');
    }
  };

  const handleLogout = () => {
    apiService.logout();
    navigate('/login');
  };

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Заголовок */}
      <div className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <span className="text-lg font-bold text-gray-900">ИИсчёт</span>
              <span className="ml-3 text-sm text-gray-400 hidden sm:inline">Мои заявки</span>
            </div>
            <div className="flex items-center gap-3">
              {subscription && subscription.active ? (
                <button
                  onClick={() => navigate('/account')}
                  className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 hover:bg-green-200"
                >
                  Подписка активна
                  {subscription.expires_at ? ` до ${new Date(subscription.expires_at).toLocaleDateString('ru-RU')}` : ''}
                </button>
              ) : (
                <button
                  onClick={() => navigate('/account')}
                  className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200"
                >
                  Подписка: нет
                </button>
              )}
              <button
                onClick={() => navigate('/account')}
                className="text-gray-700 hover:text-gray-900 text-sm font-medium"
              >
                Личный кабинет
              </button>
              {isAdmin && (
                <>
                  <span className="h-5 w-px bg-gray-200" aria-hidden="true" />
                  <button
                    onClick={() => navigate('/admin')}
                    className="text-xs font-medium text-amber-700 border border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-md px-2 py-1"
                  >
                    Админ-панель
                  </button>
                </>
              )}
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                Выход
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Онбординг: как начать работу */}
      <Onboarding />

      {/* Основная область */}
      <div className="flex-1 overflow-hidden">
        <Allotment>
          {/* Левая панель */}
          <Allotment.Pane preferredSize={320} minSize={250} maxSize={500}>
            <div className="h-full bg-white shadow-lg overflow-hidden">
              <LeftPanel
                requests={requests}
                selectedRequest={selectedRequest}
                onRequestSelect={handleRequestSelect}
                onStageSelect={(path) => setCurrentStagePath(path)}
                onUploadRequest={handleRequestUpload}
                onUploadPriceBase={async (files) => {
                  try {
                    setIsLoading(true);
                    const resp = await apiService.uploadPriceBase(files);
                    toast.success('Прайс загружен, строим базу...');
                    await new Promise<void>((resolve, reject) => {
                      const intervalId = setInterval(async () => {
                        try {
                          const pb = await apiService.getPriceBase();
                          setPriceBase(pb);
                          if (pb && pb.status === 'ready') {
                            clearInterval(intervalId);
                            resolve();
                          } else if (pb && pb.status === 'failed') {
                            clearInterval(intervalId);
                            reject(new Error('Обработка прайса завершилась с ошибкой'));
                          }
                        } catch (e) {
                          clearInterval(intervalId);
                          reject(e);
                        }
                      }, 2000);
                    });
                    toast.success('Обработка прайса завершена');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                onDownloadFile={handleDownloadFile}
                onDeleteRequest={handleDeleteRequest}
                priceBase={priceBase}
                isLoading={isLoading}
              />
            </div>
          </Allotment.Pane>

          {/* Правая часть — таблица результата / счёт */}
          <Allotment.Pane>
            <div className="h-full flex flex-col bg-white shadow-lg">
              {currentStagePath ? (
                <TableView
                  filePath={currentStagePath}
                  templateBillPath={selectedRequest?.template_bill_path}
                  mode={
                    selectedRequest?.bill_path === currentStagePath ? 'bill' :
                    selectedRequest?.merged_path === currentStagePath ? 'merged' :
                    selectedRequest?.analysis_path === currentStagePath ? 'secondary' :
                    selectedRequest?.preview_path === currentStagePath ? 'preview' : 'primary'
                  }
                  ingestMethod={selectedRequest?.ingest_method}
                  ingestWarnings={parseWarnings(selectedRequest?.ingest_warnings)}
                  onConfirmPreview={handleConfirmPreview}
                  isBusy={isConfirming}
                  onConfirmSelections={handleConfirmSelections}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 px-6 text-center">
                  Выберите заявку и этап в левой панели, чтобы увидеть результат
                </div>
              )}
            </div>
          </Allotment.Pane>
        </Allotment>
      </div>
    </div>
  );
};

export default Dashboard; 